import { ForwardedRef, useImperativeHandle } from 'react';
import { FormikProps, setNestedObjectValues } from 'formik';
import { ResponseFormHandle, collectFormErrors } from './responseForm';

type ResponseFormik<TValues> = Pick<
    FormikProps<TValues>,
    'values' | 'validateForm' | 'setTouched' | 'resetForm' | 'submitForm'
>;

type Options<TValues, TDraft> = {
    formik: ResponseFormik<TValues>;
    toDraft: (values: TValues) => TDraft;
    onSave?: (draft: TDraft) => Promise<unknown>;
    attachmentErrors: string[];
};

/**
 * Exposes `save` and `submit` on a response form's ref, the same way for every message type.
 *
 * The forms differ in their fields, not in how a draft is parked or sent, so this is what lets the
 * drawer drive any of them through one `ResponseFormHandle`.
 */
const useResponseFormHandle = <TValues, TDraft>(
    ref: ForwardedRef<ResponseFormHandle>,
    { formik, toDraft, onSave, attachmentErrors }: Options<TValues, TDraft>
) =>
    useImperativeHandle(
        ref,
        () => ({
            save: async () => {
                await onSave?.(toDraft(formik.values));
                formik.resetForm({ values: formik.values });
                const errors = await formik.validateForm(formik.values);
                formik.setTouched(setNestedObjectValues(errors, true), false);
            },
            submit: async () => {
                const errors = await formik.validateForm(formik.values);
                formik.setTouched(setNestedObjectValues(errors, true), false);

                const blocking = [...collectFormErrors(errors), ...attachmentErrors];
                if (blocking.length) return blocking;

                await formik.submitForm();
                return [];
            },
        }),
        [formik, toDraft, onSave, attachmentErrors]
    );

export default useResponseFormHandle;
